import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
  Cell
} from 'recharts';
import {
  Search, ChevronsDown, ChevronLeft, ChevronRight,
  TrendingUp, BookOpen as BookOpenIcon, BarChart2 as BarChartIcon // Renamed BookOpen to avoid conflict if any
} from 'lucide-react';

// Configuration constants
const TRADE_HISTORY_ITEMS_PER_PAGE = 10;

// Color palette (ensure this is consistent with your main Dashboard or imported)
const COLORS_PALETTE = {
  primary: '#6e56cf', primaryLight: '#9e8cfc', primaryDark: '#4e3599',
  secondary: '#10b981', tertiary: '#f97316', quaternary: '#0ea5e9',
  buy: '#10b981', sell: '#f43f5e',
  success: '#10b981', warning: '#f97316', danger: '#f43f5e', info: '#0ea5e9',
  bgGradient1: '#0f172a', bgGradient2: '#1e293b',
  cardGradient1: 'rgba(30, 41, 59, 0.8)', cardGradient2: 'rgba(15, 23, 42, 0.9)',
  chart: ['#6e56cf', '#10b981', '#f97316', '#0ea5e9', '#f43f5e', '#a855f7', '#ec4899', '#f59e0b'],
};

// Chart animations CSS (as provided by you)
const chartStyles = `
@keyframes scan {
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(1000%);
    opacity: 0;
  }
}

@keyframes pulse {
  0% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
}

.animate-scan {
  animation: scan 8s cubic-bezier(0.3, 0, 0.7, 1) infinite;
}

.animate-pulse {
  animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.scroll-hint {
  animation: fade 2s ease-in-out infinite;
}

@keyframes fade {
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}
`;

// UI Components (Card, ChartCard, Button, SearchInput need to be defined or imported if they are external)
// Assuming they are similar to what was in Dashboard.js or simplified here for focus.

const Card = ({ children, className = '' }) => (
  <div className={`relative bg-gradient-to-br from-gray-800/90 to-gray-900/95 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-xl overflow-hidden ${className}`}>
    <div className="absolute inset-0 bg-gradient-to-tr from-purple-700/5 via-transparent to-purple-500/10 pointer-events-none"></div>
    {children}
  </div>
);

const Button = ({ children, variant = 'primary', icon, className = '', disabled, ...props }) => {
  const variantClasses = { 
    primary: 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500', 
    secondary: 'bg-gray-700 hover:bg-gray-800 focus:ring-gray-500', 
    outline: 'bg-transparent border border-gray-600 hover:bg-gray-800 focus:ring-gray-500 text-gray-300 hover:text-white',
  };
  return (
    <button 
      className={`inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-200 shadow-lg ${variantClasses[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`} 
      disabled={disabled} 
      {...props}
    >
      {icon && React.cloneElement(icon, { className: `h-4 w-4 ${children ? 'mr-2' : ''}` })}
      {children}
    </button>
  );
};

const SearchInput = ({ value, onChange, placeholder = 'Search...', className = '' }) => (
  <div className={`relative w-full md:w-auto ${className}`}>
    <input 
      type="text" 
      placeholder={placeholder} 
      value={value} 
      onChange={onChange} 
      className="w-full md:w-72 pl-10 pr-4 py-2 rounded-lg bg-gray-800/60 border border-gray-700 text-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-500 text-sm transition-all" 
    />
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
  </div>
);


// Date range selector component
const DateRangeSelector = ({ selectedRange, onRangeChange }) => {
  return (
    <div className="flex items-center space-x-2 text-sm">
      <span className="text-gray-400">Time Range:</span>
      <div className="flex bg-gray-800/80 rounded-lg p-1 backdrop-blur-sm border border-gray-700/50">
        {[
          { value: '6m', label: '6M' },
          { value: '1y', label: '1Y' },
          { value: '2y', label: '2Y' },
          { value: 'all', label: 'All' }
        ].map(range => (
          <button
            key={range.value}
            className={`px-3 py-1.5 rounded-md transition-all text-xs sm:text-sm ${
              selectedRange === range.value 
                ? 'bg-purple-600/90 text-white shadow-lg' 
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
            }`}
            onClick={() => onRangeChange(range.value)}
          >
            {range.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Main component
const TradeHistoryTab = ({ tradeHistory, accountInfo, searchTerm, setSearchTerm, statistics }) => {
  const [visibleCount, setVisibleCount] = useState(TRADE_HISTORY_ITEMS_PER_PAGE);
  const [monthlyChartRange, setMonthlyChartRange] = useState('1y');

  useEffect(() => {
    if (!document.getElementById("chart-animation-styles")) {
      const styleEl = document.createElement("style");
      styleEl.id = "chart-animation-styles";
      styleEl.textContent = chartStyles;
      document.head.appendChild(styleEl);
      return () => {
        const styleElement = document.getElementById("chart-animation-styles");
        if (styleElement) {
          document.head.removeChild(styleElement);
        }
      };
    }
  }, []);

  const dailyProfitData = useMemo(() => {
    if (!tradeHistory) return [];
    const dailyMap = tradeHistory.reduce((acc, trade) => {
      if (!trade.date || typeof trade.date !== 'string') return acc;
      // Use netProfit if available, otherwise fallback to profit
      const profitForCalc = trade.netProfit !== undefined ? trade.netProfit : (trade.profit || 0);
      acc[trade.date] = (acc[trade.date] || 0) + profitForCalc;
      return acc;
    }, {});

    return Object.entries(dailyMap)
      .map(([date, profit]) => ({ date, netProfit: parseFloat(profit.toFixed(2)) }))
      .sort((a, b) => {
        try {
          // Dates are YYYY.MM.DD, convert to YYYY-MM-DD for reliable Date parsing
          const dateA = new Date(a.date.replace(/\./g, '-'));
          const dateB = new Date(b.date.replace(/\./g, '-'));
          if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0; // Handle invalid dates
          return dateA - dateB;
        } catch (e) { return 0; }
      });
  }, [tradeHistory]);

  const winLossData = useMemo(() => {
    if (!statistics) return [];
    return [
      { name: 'Wins', count: statistics.winningTrades || 0, fill: COLORS_PALETTE.success },
      { name: 'Losses', count: statistics.losingTrades || 0, fill: COLORS_PALETTE.danger },
    ];
  }, [statistics]);

  const monthlyGainsData = useMemo(() => {
    if (!tradeHistory || !accountInfo) return [];
    
    const monthlyMap = tradeHistory.reduce((acc, trade) => {
      if (!trade.date || typeof trade.date !== 'string') {
        // console.warn('Skipping trade in monthlyGainsData due to missing/invalid date:', trade);
        return acc;
      }
      
      const dateParts = trade.date.split('.'); // Corrected: Split by period
      if (dateParts.length < 2) { // Needs at least Year and Month
        // console.warn(`Skipping trade in monthlyGainsData due to unexpected date format: ${trade.date}`, trade);
        return acc;
      }

      const yearStr = dateParts[0];
      const monthStr = dateParts[1];
      
      const year = parseInt(yearStr, 10);
      const monthInt = parseInt(monthStr, 10); // This is 1-indexed (e.g., 1 for Jan)

      if (isNaN(year) || isNaN(monthInt) || monthInt < 1 || monthInt > 12) {
        // console.warn(`Skipping trade in monthlyGainsData due to invalid year/month from date: ${trade.date}`, trade);
        return acc;
      }
      
      const monthKey = `${yearStr}-${monthStr.padStart(2, '0')}`; // Ensures YYYY-MM format, e.g., "2023-05"
      
      if (!acc[monthKey]) {
        try {
          const dateForLabel = new Date(year, monthInt - 1, 1); // monthInt - 1 for 0-indexed month for Date constructor
          if (isNaN(dateForLabel.getTime())) {
            // console.warn(`Generated Invalid Date for displayLabel. monthKey: ${monthKey}, from trade.date: ${trade.date}`);
            return acc; // Skip if date generation failed
          }
          acc[monthKey] = {
            month: monthKey,
            profit: 0,
            count: 0,
            displayLabel: dateForLabel.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
          };
        } catch (e) {
            // console.warn(`Error creating date for displayLabel with ${yearStr}-${monthStr}-01`, e);
            return acc;
        }
      }
      
      // Use netProfit if available, otherwise fallback to profit
      const profitForCalc = trade.netProfit !== undefined ? trade.netProfit : (trade.profit || 0);
      acc[monthKey].profit += profitForCalc;
      acc[monthKey].count += 1;
      
      return acc;
    }, {});
    
    const baseBalance = (accountInfo && typeof accountInfo.balance === 'number' && accountInfo.balance > 0) 
                        ? accountInfo.balance 
                        : 10000; // Fallback for percentage calculation if balance is 0 or unavailable
    
    const monthlyValues = Object.values(monthlyMap).map(entry => {
      const percentageGain = baseBalance !== 0 ? (entry.profit / baseBalance) * 100 : 0;
      return {
        ...entry,
        percentGain: parseFloat(percentageGain.toFixed(2))
      };
    });
    
    return monthlyValues.sort((a, b) => a.month.localeCompare(b.month));
  }, [tradeHistory, accountInfo]);

  const filteredMonthlyGainsData = useMemo(() => {
    if (!monthlyGainsData.length) return [];
    
    const now = new Date();
    let filterDateStart = new Date();
    
    switch (monthlyChartRange) {
      case '6m':
        filterDateStart.setMonth(now.getMonth() - 6);
        filterDateStart.setDate(1); // Start of the month
        break;
      case '1y':
        filterDateStart.setFullYear(now.getFullYear() - 1);
        filterDateStart.setDate(1);
        break;
      case '2y':
        filterDateStart.setFullYear(now.getFullYear() - 2);
        filterDateStart.setDate(1);
        break;
      case 'all':
      default:
        return monthlyGainsData;
    }
    // Normalize filterDateStart to YYYY-MM format for comparison
    const filterMonthKeyStart = `${filterDateStart.getFullYear()}-${String(filterDateStart.getMonth() + 1).padStart(2, '0')}`;
    
    return monthlyGainsData.filter(item => item.month >= filterMonthKeyStart);
  }, [monthlyGainsData, monthlyChartRange]);

  const sortedAndFilteredTrades = useMemo(() => {
    if (!tradeHistory) return [];
    let filtered = tradeHistory;
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = tradeHistory.filter(trade =>
          Object.values(trade).some(val => 
            val !== undefined && val !== null && String(val).toLowerCase().includes(lowerSearchTerm)
          )
      );
    }
    return [...filtered].sort((a, b) => {
      try {
        const dateStrA = `${String(a.date || '').replace(/\./g, '-')}T${a.time || '00:00:00'}`;
        const dateStrB = `${String(b.date || '').replace(/\./g, '-')}T${b.time || '00:00:00'}`;
        const dateA = new Date(dateStrA);
        const dateB = new Date(dateStrB);
        if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;
        return dateB - dateA; // Sort descending (newest first)
      } catch (e) { return 0; }
    });
  }, [tradeHistory, searchTerm]);

  const tradesToShow = sortedAndFilteredTrades.slice(0, visibleCount);

  return (
    <div className="space-y-6">
      <Card className="relative overflow-hidden">
        <div className="p-5 flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center border-b border-gray-700/20">
          <h3 className="flex items-center text-lg font-semibold text-gray-100">
            <TrendingUp className="h-5 w-5 mr-2 text-purple-400" />
            Monthly Performance (Gain/Loss %)
          </h3>
          <DateRangeSelector
            selectedRange={monthlyChartRange}
            onRangeChange={setMonthlyChartRange}
          />
        </div>
        <div className="p-5 pt-3 h-96 relative">
          {/* ... (decorative elements as before) ... */}
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={filteredMonthlyGainsData} 
              margin={{ top: 32, right: 20, left: 5, bottom: 30 }} // Adjusted left margin
            >
              <defs>
                <linearGradient id="positiveGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS_PALETTE.success} stopOpacity={1}/>
                  <stop offset="100%" stopColor={COLORS_PALETTE.success} stopOpacity={0.7}/>
                </linearGradient>
                <linearGradient id="negativeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS_PALETTE.danger} stopOpacity={1}/>
                  <stop offset="100%" stopColor={COLORS_PALETTE.danger} stopOpacity={0.7}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} vertical={false} />
              <XAxis 
                dataKey="displayLabel" 
                stroke="#9ca3af" 
                tick={{fill: '#d1d5db', fontSize: 11}} 
                axisLine={{stroke: '#374151'}}
                tickLine={{stroke: '#374151'}}
                interval={'preserveStartEnd'}
                angle={filteredMonthlyGainsData.length > 7 ? -40 : (filteredMonthlyGainsData.length > 4 ? -20 : 0)}
                textAnchor={filteredMonthlyGainsData.length > 4 ? "end" : "middle"}
                height={filteredMonthlyGainsData.length > 4 ? 55 : 40}
                // allowDataOverflow={filteredMonthlyGainsData.length > 6} // Might not be needed with angle
              />
              <YAxis 
                stroke="#9ca3af" 
                tick={{fill: '#d1d5db', fontSize: 11}} 
                tickFormatter={(value) => `${value}%`}
                axisLine={{stroke: '#374151'}}
                tickLine={{stroke: '#374151'}}
                domain={['auto', 'auto']}
                width={45} // Adjusted width
              />
              <Tooltip 
                formatter={(value, name, props) => [`${parseFloat(props.payload.profit?.toFixed(2))} (${value.toFixed(2)}%)`, 'Monthly P/L']}
                contentStyle={{ 
                  backgroundColor: 'rgba(17, 24, 39, 0.95)', 
                  backdropFilter: 'blur(8px)', 
                  border: '1px solid rgba(75, 85, 99, 0.5)', 
                  borderRadius: '0.75rem', 
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)', 
                  color: '#e5e7eb',
                  padding: '12px'
                }} 
                cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                itemStyle={{ color: '#d1d5db' }}
                labelStyle={{ color: '#9ca3af', fontWeight: 'bold', marginBottom: '8px' }}
              />
              <ReferenceLine y={0} stroke="#6B7280" strokeWidth={1} />
              <Bar 
                dataKey="percentGain" 
                name="Monthly Gain (%)" 
                radius={[6, 6, 0, 0]}
                barSize={Math.max(12, Math.min(35, 350 / Math.max(1, filteredMonthlyGainsData.length)))}
                animationDuration={1200}
                animationEasing="ease-out"
              >
                {filteredMonthlyGainsData.map((entry, index) => (
                  <Cell 
                    key={`cell-monthly-${index}`} 
                    fill={entry.percentGain >= 0 ? 'url(#positiveGradient)' : 'url(#negativeGradient)'} 
                    style={{ 
                      filter: `drop-shadow(0 0 6px ${entry.percentGain >= 0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(244, 63, 94, 0.3)'})`, 
                      strokeWidth: 1, 
                      stroke: entry.percentGain >= 0 ? 'rgba(16, 185, 129, 0.6)' : 'rgba(244, 63, 94, 0.6)' 
                    }}
                  />
                ))}
              </Bar>
              {filteredMonthlyGainsData.length <= 8 && ( // Show labels for fewer bars
                <Bar 
                  dataKey="percentGain" name="" fill="transparent" stroke="transparent"
                  label={{
                    position: 'top',
                    fill: d => d.percentGain >= 0 ? COLORS_PALETTE.success : COLORS_PALETTE.danger,
                    formatter: (value) => `${value.toFixed(1)}%`,
                    fontSize: 10, fontWeight: 500, offset: 8,
                    style: { textShadow: '0 0 8px rgba(0,0,0,0.4)' }
                  }}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
          {filteredMonthlyGainsData.length > 7 && ( // Adjust based on typical screen width for your app
            <div className="text-xs text-gray-400 text-center mt-1 scroll-hint flex items-center justify-center space-x-1">
              <ChevronLeft size={14} />
              <span>Scroll chart for more months</span>
              <ChevronRight size={14} />
            </div>
          )}
        </div>
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent animate-scan"></div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-0"> {/* ChartCard equivalent styling */}
          <div className="p-5"><h3 className="flex items-center text-lg font-semibold text-gray-100 mb-4"><TrendingUp className="h-5 w-5 mr-2 text-purple-400" />Daily Net Profit</h3></div>
          <div className="p-5 pt-0 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyProfitData} margin={{ top: 5, right: 20, left: -15, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 9}} 
                  interval="preserveStartEnd"
                  angle={dailyProfitData.length > 10 ? -45 : 0}
                  textAnchor={dailyProfitData.length > 10 ? "end" : "middle"}
                  height={40}
                />
                <YAxis stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 9}} tickFormatter={(value) => value.toLocaleString()} width={55}/>
                <Tooltip formatter={(value) => [`${accountInfo.currency} ${value.toFixed(2)}`, "Net Profit"]} contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '0.5rem', color: '#e5e7eb' }} />
                <defs>
                    <linearGradient id="dailyProfitGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS_PALETTE.primary} stopOpacity={0.6}/>
                    <stop offset="95%" stopColor={COLORS_PALETTE.primary} stopOpacity={0.05}/>
                    </linearGradient>
                </defs>
                <Area type="monotone" dataKey="netProfit" stroke={COLORS_PALETTE.primary} strokeWidth={2} fill="url(#dailyProfitGradient)" />
                <ReferenceLine y={0} stroke={COLORS_PALETTE.tertiary} strokeWidth={1} strokeDasharray="3 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        
        <Card className="p-0"> {/* ChartCard equivalent styling */}
          <div className="p-5"><h3 className="flex items-center text-lg font-semibold text-gray-100 mb-4"><BarChartIcon className="h-5 w-5 mr-2 text-purple-400" />Win vs. Loss Count</h3></div>
          <div className="p-5 pt-0 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={winLossData} layout="vertical" margin={{left:-5, right:30, top: 5, bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false}/>
                <XAxis type="number" stroke="#9ca3af" tick={{fill: '#9ca3af', fontSize: 10}} />
                <YAxis type="category" dataKey="name" stroke="#9ca3af" tick={{fill: '#d1d5db', fontSize: 12}} width={55}/>
                <Tooltip contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.8)', border: '1px solid rgba(75, 85, 99, 0.5)', borderRadius: '0.5rem', color: '#e5e7eb' }} />
                <Bar dataKey="count" name="Count" radius={[0, 4, 4, 0]} barSize={30}>
                    {winLossData.map((entry, index) => (
                      <Cell key={`cell-wl-${index}`} fill={entry.fill} />
                    ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="p-5 flex flex-col md:flex-row justify-between items-center border-b border-gray-700 gap-4">
          <h3 className="flex items-center text-lg font-semibold text-gray-100"><BookOpenIcon className="h-5 w-5 mr-2 text-purple-400" />Trade History Log</h3>
          <SearchInput value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search history..." />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-800/50"><tr>
              {["ID", "Date", "Time", "Symbol", "Type", "Volume", "Profit", "Pips", "Notes"].map(h => ( // Removed Duration for brevity, add back if needed
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{h}</th>
              ))}
            </tr></thead>
            <tbody className="bg-transparent divide-y divide-gray-700/50">
              {tradesToShow.map(t => (
                <tr key={t.id || t.ticket} className="hover:bg-gray-800/30 transition-colors duration-150">
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">{t.id || t.ticket}</td>
                  <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">{t.date || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">{t.time || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm font-medium text-white whitespace-nowrap">{t.symbol}</td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap"><span className={`font-semibold ${String(t.type).toLowerCase() === 'buy' ? 'text-buy' : 'text-sell'}`}>{t.type}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">{t.volume?.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${(t.netProfit !== undefined ? t.netProfit : (t.profit || 0)) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {accountInfo.currency} {(t.netProfit !== undefined ? t.netProfit : (t.profit || 0))?.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">{t.pips?.toFixed(1) || 'N/A'}</td>
                  {/* <td className="px-4 py-3 text-sm text-gray-300">{t.duration || 'N/A'}</td> */}
                  <td className="px-4 py-3 text-sm text-gray-400 max-w-xs truncate" title={t.notes}>{t.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sortedAndFilteredTrades.length === 0 && (<div className="p-5 text-center text-gray-500">{searchTerm ? "No trades match your search." : "No trade history available."}</div>)}
        {visibleCount < sortedAndFilteredTrades.length && (
          <div className="p-4 text-center border-t border-gray-700/50">
            <Button variant="outline" onClick={() => setVisibleCount(prev => prev + TRADE_HISTORY_ITEMS_PER_PAGE)} icon={<ChevronsDown />}>
              View More Trades ({sortedAndFilteredTrades.length - visibleCount} remaining)
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default TradeHistoryTab;